import {
  Partner, User, UserApi,
} from '@compassdigital/foodworks.ts.sdk';
import { filter, orderBy, find } from 'lodash';
import Format from '@/util/Format';
import MarketUtil from '@/util/MarketUtil';
import UserModule from '@/store/modules/user';
import AppModule from '@/store/modules/app';
import { IUserMappedInfo } from '@/interface/marketInfo';

export default class PartnerUtil {
  /**
   * Parses the status and returns color
   * @param status
   */
  public static getColor(status: string) {
    switch (status) {
      case 'awaitScout':
        return 'red';
      case 'completed':
        return 'green small';
      case 'awaitPartner':
        return 'orange';
      default:
        return '';
    }
  }

  /**
   * Parses the Partner Type and returns formatted Partner Name
   * @param status
   */
  public static getStatus(status: string) {
    const role = UserModule.currentRole;
    switch (status) {
      case 'awaitPartner':
        // if (role === 'partner_owner') {
        //   return 'Action required';
        // }
        return 'In-progress';
      case 'completed':
        return 'Approved';
      case 'awaitScout':
        return 'Pending approval';
      default:
        return 'In-progress';
    }
  }

  /**
   * Parses the Partner status and returns formatted status
   * @param partnerType
   */
  public static getPartnerName(partnerType: string) {
    switch (partnerType) {
      case 'restaurant':
        return 'Restaurant';
      case 'foodtruck':
        return 'Food Truck';
      case 'apron_exchange':
        return 'Apron Exchange';
      default:
        return '';
    }
  }

  /**
   * Parses the partners, markets and returns formatted array
   * @param partners
   */
  public static getMappedPartners(
    partners: any, markets: any, loggedUser: string, scoutItems: any,
  ) {
    const mappedPartners = !partners
      || partners.map((partner: any) => {
        const {
          name,
          _createdDate,
          id,
          marketId,
          onboarding,
          scoutUserId,
          cuisineTypes,
          type,
        } = partner;
        const partnerInfo: any = {};
        partnerInfo.marketName = MarketUtil.getMarketNameByID(
          markets,
          marketId,
        );
        partnerInfo.scoutName = MarketUtil.getMarketNameByID(
          scoutItems,
          scoutUserId,
        );
        partnerInfo.cuisineType = Format.getFormatCuisine(cuisineTypes);
        partnerInfo.status = this.getStatus(onboarding.status);
        partnerInfo.userStatusColor = this.getColor(onboarding.status);
        partnerInfo.id = id;
        partnerInfo.marketID = marketId;
        partnerInfo.scoutID = scoutUserId || null;
        partnerInfo.partnerName = name;
        partnerInfo.createdAt = _createdDate;
        partnerInfo.type = Format.getFormatType(type);

        return partnerInfo;
      });
    return this.makeOrder(mappedPartners, loggedUser);
  }

  /**
   * @param partners
   * @param user
   */
  public static makeOrder(partners: any, user: string) {
    const list: any = [];
    list.push(...filter(partners, p => p.scoutName === user));
    list.push(...filter(partners, p => p.scoutName !== user));

    return orderBy(list, 'createdAt', 'desc');
  }

  /**
   * Parses the partner, markets and returns formatted partner object
   * @param partner
   * TODO: should define the return object of this method
   */
  public static getMappedPartner(
    partner: any,
    markets: any,
    scouts: any = [],
  ):
    IUserMappedInfo {
    if (!partner) return {};
    const {
      active,
      address,
      ownerUserId,
      cuisineTypes,
      id,
      marketId,
      type,
      onboarding,
      businessNumber,
      scoutUserId,
      name,
      website,
      deliveryPartners,
      foodPrepLocation,
      mobileOperations,
      seatingCapicity,
      foodPrepLocOther,
      auditFormId,
      isMinorityOwned,
      isWMBECertified,
      isWomenOwned,
      _relationship,
    } = partner;

    const marketName = _relationship?.marketName
      || (marketId ? MarketUtil.getMarketNameByID(markets, marketId!) : '');

    let userMarketID: string = '';
    if (marketId) {
      userMarketID = marketId;
    }

    const scoutName = _relationship?.scoutName
      || (scoutUserId ? MarketUtil.getScoutNameByID(scouts, scoutUserId) : '');

    return {
      active,
      ownerUserId,
      scoutUserId,
      id,
      restaurant: {
        id,
        address,
        name,
        website,
        cuisineTypes: this.cuisineArray(cuisineTypes),
        businessNumber: this.checkNumber(businessNumber.phone),
        type,
        deliveryPartners: this.filterDeliveryPartners(deliveryPartners),
        foodPrepLocation: this.cuisineArray(foodPrepLocation),
        mobileOperations,
        seatingCapicity,
        foodPrepLocOther,
        auditFormId,
        isMinorityOwned: isMinorityOwned || false,
        isWMBECertified: isWMBECertified || false,
        isWomenOwned: isWomenOwned || false,
      },
      status: onboarding.status,
      admin: {
        partnerID: id,
        marketId: userMarketID,
        market: userMarketID,
        scoutID: scoutUserId || null,
        scoutName,
        marketName,
        partnerType: type,
        partnerTypeName: this.getPartnerName(type),
        status: onboarding.status,
      },
    };
  }

  /**
   * Parses the partners and returns scout array
   * @param partner
   */
  public static getMappedScouts(partner: any) {
    if (!partner) return null;

    const mappedScouts = !partner
      || partner.map((value: any) => {
        const { scout } = value;

        const { name } = scout;

        let scoutName = '';

        if (name && name.first && name.last) {
          scoutName = Format.capitalize(`${name.first} ${name.last}`);
        }

        return {
          text: scoutName,
          value: scoutName,
        };
      });
    return mappedScouts.filter((ms: { value: string }) => ms.value !== '');
  }

  /**
   * Parses the documents and returns mapped documents
   * @param documents
   */
  public static getMappedDocuments(documents: any) {
    return (
      !documents
      || documents.map((doc: any) => {
        const {
          id,
          base64Encoded,
          name,
          type,
          size,
          expirationDate,
          uploadedDate,
        } = doc;
        return {
          base64Encoded,
          id,
          name,
          type,
          size,
          expirationDate,
          uploadedDate,
          isUploaded: true,
        };
      })
    );
  }

  public static getPartnerStatusColor(status: string) {
    if (!status) return '';
    const partnerStatus: any = {};
    partnerStatus.status = this.getStatus(status);
    partnerStatus.color = this.getColor(status);
    return partnerStatus;
  }

  /**
   * partner
   * @param partner
   */
  public static getMappedOnboardingUser(partner: Partner) {
    if (!partner) return {};
    const {
      active,
      address,
      ownerUserId,
      cuisineTypes,
      id,
      onboarding,
      scoutUserId,
      name,
      website,
      businessNumber,
    } = partner;
    return {
      active,
      ownerUserId,
      scoutUserId,
      id,
      address,
      name,
      website,
      cuisineTypes: this.cuisineArray(cuisineTypes),
      status: onboarding!.status,
      businessNumber: this.checkNumber(businessNumber!.phone),
    };
  }

  /**
   * partner
   * @param user
   */
  public static mappedOnboardingUser(user: any) {
    if (!user) return {};
    const {
      password,
      permissions,
      _lastUpdatedDate,
      is,
      id,
      _createdDate,
      email,
      name,
      phone,
      secrets,
      title,
    } = user;
    return {
      password,
      permissions,
      _lastUpdatedDate,
      is,
      id,
      _createdDate,
      email,
      name,
      phone,
      secrets,
      title: this.getUserStatus(title),
    };
  }

  /**
   * checkNumber
   * @param phone
   */
  public static checkNumber(numberPhone: any) {
    const buisinessPhone: any = {
      phone: '',
    };
    if (numberPhone) {
      buisinessPhone.phone = numberPhone;
      return buisinessPhone;
    }
    return buisinessPhone;
  }

  /**
   * cuisineArray
   * @param cuisines
   */
  public static cuisineArray(cuisines: any) {
    if (cuisines) {
      const updatedCuisines = cuisines.toString();
      return updatedCuisines;
    }
    return '';
  }

  /**
   * Removes deprecated "None of the above" from delivery partners returned by the API.
   * @param deliveryPartners
   */
  public static filterDeliveryPartners(deliveryPartners: string[] | undefined) {
    if (!Array.isArray(deliveryPartners)) {
      return deliveryPartners;
    }
    return deliveryPartners.filter((partner) => partner !== 'None of the above');
  }

  /**
   * Parses the onBoarding user status and returns formatted status
   * @param userType
   */
  public static getUserStatus(userType: string) {
    switch (userType) {
      case 'partner_owner':
        return 'Partner';
      default:
        return '';
    }
  }

  /**
   * Parses the users and returns formatted users list
   * @param users
   */
  public static usersListFormatted(users: Array<User>) {
    if (!users) return '';
    const activeUsers = !users || users.filter(
      user => user.is?.disabled === false && user.permissions?.scopes.includes('partner_owner'),
    );
    return Format.usersListFormatted(activeUsers);
  }

  /**
   * Parses the owner user id and returns email of the owner user
   * @param ownerID
   */
  public static async getPartnerEmailById(ownerID: string) {
    try {
      const api = new UserApi(AppModule.apiConfig);
      const partnerData = await api.getUser(ownerID);
      const { email } = partnerData.data;
      return email;
    } catch (err) {
      throw err;
    }
  }

  /**
   * Parses the owner scout id and returns Active Scout ID
   * @param ownerID
   */
  public static async getScouts(scoutID: string) {
    try {
      const api = new UserApi(AppModule.apiConfig);
      const users = await api.getUser(scoutID);
      const {
        id, phone, email, name,
      } = users.data;

      return {
        id,
        phone,
        email,
        name: Format.getFormattedName(name),
      };
    } catch (err) {
      throw err;
    }
  }
}
